import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Search as SearchIcon, FilterList as FilterIcon } from '@mui/icons-material';
import { Etablissement, SearchFilters } from '../types';
import { airtableService } from '../services/airtableService';

interface EtablissementSearchProps {
  onEtablissementSelect: (etablissement: Etablissement) => void;
}

export const EtablissementSearch: React.FC<EtablissementSearchProps> = ({
  onEtablissementSelect,
}) => {
  const [etablissements, setEtablissements] = useState<Etablissement[]>([]);
  const [filteredEtablissements, setFilteredEtablissements] = useState<Etablissement[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<SearchFilters>({});
  const [formOptions, setFormOptions] = useState({
    departements: [] as string[],
    typesEtablissement: [] as string[],
    academies: [] as string[],
  });
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Charger les options de formulaire au montage
  useEffect(() => {
    loadFormOptions();
  }, []);

  // Charger tous les établissements au montage (liste complète)
  useEffect(() => {
    loadEtablissements();
  }, []);

  // Recharger si les filtres changent
  useEffect(() => {
    if (filters.departement || filters.typeEtablissement || filters.academie) {
      loadEtablissements();
    }
  }, [filters]);

  // Filtrer dynamiquement selon la recherche
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredEtablissements(etablissements);
    } else {
      const filtered = etablissements.filter(etablissement =>
        etablissement.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        etablissement.commune.toLowerCase().includes(searchTerm.toLowerCase()) ||
        etablissement.departement.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEtablissements(filtered);
    }
  }, [searchTerm, etablissements]);

  const loadFormOptions = async () => {
    try {
      const options = await airtableService.getFormOptions();
      setFormOptions(options);
    } catch (error) {
      console.error('Erreur lors du chargement des options:', error);
    }
  };

  const loadEtablissements = async () => {
    try {
      setLoading(true);
      const data = await airtableService.getEtablissements(filters);
      setEtablissements(data);
    } catch (error) {
      console.error('Erreur lors du chargement des établissements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field: keyof SearchFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const handleEtablissementSelect = (etablissement: Etablissement) => {
    onEtablissementSelect(etablissement);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Recherche d'établissement
      </Typography>

      {/* Barre de recherche */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          label="Rechercher un établissement..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          placeholder="Nom, commune ou département..."
        />
      </Box>

      {/* Bouton filtres */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<FilterIcon />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{
            bgcolor: '#F5F5F5',
            color: '#333',
            borderColor: '#E0E0E0',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#e0e0e0',
              borderColor: '#bdbdbd',
            },
          }}
        >
          {showFilters ? 'Masquer les filtres' : 'Afficher les filtres'}
        </Button>
      </Box>

      {/* Filtres */}
      {showFilters && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filtres
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Département</InputLabel>
                  <Select
                    value={filters.departement || ''}
                    onChange={(e) => handleFilterChange('departement', e.target.value)}
                    label="Département"
                  >
                    <MenuItem value="">Tous les départements</MenuItem>
                    {formOptions.departements.map((dept) => (
                      <MenuItem key={dept} value={dept}>
                        {dept}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                sx={{
                  bgcolor: '#F5F5F5',
                  color: '#333',
                  borderColor: '#E0E0E0',
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#e0e0e0',
                    borderColor: '#bdbdbd',
                  },
                }}
              >
                Effacer les filtres
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Résultats */}
      <Box>
        <Typography variant="h6" gutterBottom>
          Résultats ({filteredEtablissements.length} établissement(s))
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={2} justifyContent="flex-start">
            {filteredEtablissements.map((etablissement, idx) => (
              <Grid
                item
                xs={12}
                sm={filteredEtablissements.length === 1 ? 12 : filteredEtablissements.length === 2 ? 6 : 6}
                md={filteredEtablissements.length === 1 ? 12 : filteredEtablissements.length === 2 ? 6 : 4}
                key={etablissement.id}
              >
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 3 }
                  }}
                  onClick={() => handleEtablissementSelect(etablissement)}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      <span style={{ fontSize: 36, verticalAlign: 'middle' }}>🏫</span> {etablissement.nom}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {etablissement.type}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {etablissement.adresse}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {etablissement.codePostal} {etablissement.commune}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {!loading && filteredEtablissements.length === 0 && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Typography variant="body1" color="text.secondary">
              Aucun établissement trouvé
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}; 